import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getRowStyle } from "../utils/utils";
import { createTransfer, QCLABhold, releaseWorkOrder } from "../api/workOrder";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";

const OnHold = ({ workOrders: propWorkOrders, isReadOnly }) => {
  const navigate = useNavigate();
  const [selectedWorkOrders, setSelectedWorkOrders] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  const [workOrderToRelease, setWorkOrderToRelease] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiWorkOrders, setApiWorkOrders] = useState([]);
  const pageSize = 20;
  const { canWrite } = useAuth();

  const canPlaceOnHold = canWrite("ERPagOnHold");
  const canTransferCompleted = canWrite("ERPagOnHold");

  // Fetch work orders from QCLABhold API
  useEffect(() => {
    const fetchHoldWorkOrders = async () => {
      setIsLoading(true);
      try {
        const response = await QCLABhold();
        const fetchedOrders = response.data?.data || [];
        const mappedOrders = fetchedOrders.map((wo) => ({
          _id: wo._id,
          woNumber: wo.woNumber,
          scheduledTime:  wo.slot,
          projectNumber: wo.projectNumber,
          sku: wo.sku,
          title: wo.title,
          labelCategory: wo.label || "N/A",
          allergen: wo.allergens || "None",
          netWeight: `${wo.netWeight}` || "N/A",
          plannedQty: parseFloat(wo.plannedQty) || 0,
          actualQty: parseFloat(wo.actualQty) || 0,
          lotNumber: wo.lotNumber || "N/A",
          warehouse: wo.warehouse === "LAB HOLD" ? "LAB HOLD" : "QC HOLD",
          releaseStatus: wo.released, 
        }));
        setApiWorkOrders(mappedOrders);
        setApiError(null);
      } catch (error) {
        setApiError(error.message || "Failed to fetch hold work orders");
        setApiWorkOrders([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHoldWorkOrders();
  }, []);

  const filteredWorkOrders = useMemo(() => {
    const data =
      propWorkOrders &&
      Array.isArray(propWorkOrders) &&
      propWorkOrders.length > 0
        ? propWorkOrders
        : apiWorkOrders;
    return data.filter((wo) => ["LAB HOLD", "QC HOLD"].includes(wo.warehouse));
  }, [propWorkOrders, apiWorkOrders]);

  const totalPages = Math.ceil(filteredWorkOrders.length / pageSize) || 1;

  const paginatedWorkOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredWorkOrders.slice(startIndex, endIndex);
  }, [filteredWorkOrders, currentPage]);

  const paginate = useCallback(
    (pageNumber) => {
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
      }
    },
    [totalPages]
  );

  const handleSelectWorkOrder = (woNumber) => {
    setSelectedWorkOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(woNumber)) {
        newSet.delete(woNumber);
      } else {
        newSet.add(woNumber);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const eligibleWorkOrders = paginatedWorkOrders.filter(
      (wo) => wo.releaseStatus
    );
    if (selectedWorkOrders.size === eligibleWorkOrders.length) {
      setSelectedWorkOrders(new Set());
    } else {
      setSelectedWorkOrders(
        new Set(eligibleWorkOrders.map((wo) => wo.woNumber))
      );
    }
  };

  const handleReleaseWorkOrder = (workOrder) => {
    if (workOrder.releaseStatus) {
      setErrorMessage(`Work order ${workOrder.woNumber} is already released.`);
      return;
    }
    setWorkOrderToRelease(workOrder);
    setIsReleaseModalOpen(true);
  };

  const confirmRelease = async () => {
    try {
      await releaseWorkOrder([
        { orderId: workOrderToRelease._id, released: true },
      ]);

      setIsReleaseModalOpen(false);
      setWorkOrderToRelease(null);
      setErrorMessage("");

      // Refresh work orders to reflect updated status
      const response = await QCLABhold();
      const fetchedOrders = response.data?.data || [];
      const mappedOrders = fetchedOrders.map((wo) => ({
        _id: wo._id,
        woNumber: wo.woNumber,
        scheduledTime:   wo.slot,
        projectNumber: wo.projectNumber,
        sku: wo.sku,
        title: wo.title,
        labelCategory: wo.label || "N/A",
        allergen: wo.allergens || "None",
        netWeight: `${wo.netWeight}` || "N/A",
        plannedQty: parseFloat(wo.plannedQty) || 0,
        actualQty: parseFloat(wo.actualQty) || 0,
        lotNumber: wo.lotNumber || "N/A",
        warehouse: wo.warehouse === "LAB HOLD" ? "LAB HOLD" : "QC HOLD",
        releaseStatus: wo.released, // Use the 'released' field directly
      }));

      setApiWorkOrders(mappedOrders);
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message ||
          err.message ||
          `Failed to release work order ${workOrderToRelease?.woNumber}.`
      );
    }
  };

  const handleTransferToStore = async () => {
    if (selectedWorkOrders.size === 0) {
      setErrorMessage("Please select at least one released work order.");
      return;
    }
    const nonReleasedOrders = [...selectedWorkOrders].filter((woNumber) => {
      const wo = filteredWorkOrders.find((w) => w.woNumber === woNumber);
      return wo && !wo.releaseStatus;
    });
    if (nonReleasedOrders.length > 0) {
      setErrorMessage(
        "Cannot transfer work orders that have not been released."
      );
      return;
    }
    setIsTransferModalOpen(true);
  };

  const confirmTransfer = async () => {
    try {
      const orderIds = [...selectedWorkOrders]
        .map(
          (woNumber) =>
            filteredWorkOrders.find((w) => w.woNumber === woNumber)?._id
        )
        .filter((id) => id);
      if (orderIds.length === 0) {
        setErrorMessage("No valid work orders selected for transfer.");
        setIsTransferModalOpen(false);
        return;
      }
      await createTransfer({ orderId: orderIds });
      setSelectedWorkOrders(new Set());
      setIsTransferModalOpen(false);
      setErrorMessage("");
      navigate("/transfers");
    } catch (err) {
      setErrorMessage(err.message || "Failed to initiate transfer.");
    }
  };

  const closeModal = () => {
    setIsReleaseModalOpen(false);
    setIsTransferModalOpen(false);
    setWorkOrderToRelease(null);
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg border border-gray-200">
      <h3 className="text-lg sm:text-xl font-semibold mb-4 pb-3 border-b border-gray-200 text-gray-800">
        QC and LAB Hold Work Orders
      </h3>

      {errorMessage && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 text-sm rounded border border-red-300">
          {errorMessage}
        </div>
      )}

      {apiError && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 text-sm rounded border border-red-300">
          {apiError}
        </div>
      )}

      <div className="mb-4 flex justify-end gap-2 sm:gap-4">
        <button
          onClick={handleTransferToStore}
          disabled={
            isReadOnly ||
            paginatedWorkOrders.length === 0 ||
            selectedWorkOrders.size === 0 ||
            !canTransferCompleted
          }
          className={`py-1 sm:py-2 px-3 sm:px-4 rounded bg-black hover:bg-gray-700 text-white text-xs sm:text-sm font-semibold transition
    disabled:bg-gray-300 disabled:cursor-not-allowed
    ${!canTransferCompleted ? "opacity-70" : ""}`}
          title={
            !canTransferCompleted
              ? "You do not have permission to transfer released work orders"
              : ""
          }
          aria-label="Transfer selected work orders to store"
        >
          Transfer to Store
        </button>
      </div>

      {isLoading ? (
        <div
          className="text-center py-8 sm:py-12 text-gray-600 text-sm sm:text-base"
          aria-label="Loading on-hold work orders"
        >
          <div className="text-center py-12">
            <ArrowPathIcon className="mx-auto h-8 w-8 text-blue-600 mb-4 animate-spin" />
            <p className="text-gray-600 text-sm">
              Loading on-hold work orders...
            </p>
          </div>
        </div>
      ) : apiError ? (
        <div
          className="text-center py-8 sm:py-12 text-red-600 text-sm sm:text-base"
          aria-label="Error loading on-hold work orders"
        >
          {apiError}
        </div>
      ) : (
        <>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full border-collapse mt-4">
              <thead>
                <tr>
                  <th className="py-2 sm:py-3 px-2 sm:px-4 text-left bg-gray-200 text-gray-900 font-semibold uppercase text-xs border-b border-gray-300">
                    <input
                      type="checkbox"
                      checked={
                        selectedWorkOrders.size ===
                          paginatedWorkOrders.filter((wo) => wo.releaseStatus)
                            .length &&
                        paginatedWorkOrders.filter((wo) => wo.releaseStatus)
                          .length > 0
                      }
                      onChange={handleSelectAll}
                      disabled={
                        isReadOnly ||
                        paginatedWorkOrders.filter((wo) => wo.releaseStatus)
                          .length === 0
                      }
                      aria-label="Select all released work orders for transfer"
                    />
                  </th>
                  {[
                    "Scheduled Hour",
                    "WO Number",
                    "Project #",
                    "SKU",
                    "Title",
                    "Label",
                    "Allergen",
                    "Net Weight",
                    "Planned Qty",
                    "Actual Qty",
                    "Lot Number",
                    "Warehouse",
                    "Release Status",
                    "Action",
                  ].map((header) => (
                    <th
                      key={header}
                      className="py-2 sm:py-3 px-2 sm:px-4 text-left bg-gray-200 text-gray-900 font-semibold uppercase text-xs border-b border-gray-300"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedWorkOrders.length > 0 ? (
                  paginatedWorkOrders.map((wo) => (
                    <tr
                      key={wo.woNumber}
                      className="transition-all duration-200 opacity-70 hover:opacity-100"
                      style={getRowStyle(wo.allergen, wo.warehouse, null)}
                      tabIndex={0}
                      aria-label={`Work Order ${wo.woNumber}, Warehouse: ${wo.warehouse}`}
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                        <input
                          type="checkbox"
                          checked={selectedWorkOrders.has(wo.woNumber)}
                          onChange={() => handleSelectWorkOrder(wo.woNumber)}
                          disabled={isReadOnly || !wo.releaseStatus}
                          title={
                            wo.releaseStatus
                              ? "Selectable for transfer"
                              : "Must be released before transfer"
                          }
                          aria-label={`Select work order ${wo.woNumber} for transfer`}
                        />
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.scheduledTime}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.woNumber}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.projectNumber}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.sku || "N/A"}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.title}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.labelCategory || "N/A"}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.allergen || "None"}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.netWeight || "N/A"}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.plannedQty || 0}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.actualQty || 0}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        {wo.lotNumber || "N/A"}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        <span
                          className={`inline-block px-2 sm:px-3 py-1 rounded text-xs font-semibold text-gray-900 ${
                            wo.warehouse === "LAB HOLD"
                              ? "bg-[#fef08a]"
                              : "bg-[#f1bd99]"
                          }`}
                          title={
                            wo.warehouse === "LAB HOLD"
                              ? "On Lab Hold"
                              : "On QC Hold"
                          }
                          aria-label={`Warehouse: ${wo.warehouse}`}
                        >
                          {wo.warehouse}
                        </span>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900 select-text">
                        <span
                          className={`inline-block px-2 sm:px-3 py-1 rounded text-xs font-semibold text-gray-900 ${
                            wo.releaseStatus ? "bg-green-200" : "bg-red-200"
                          }`}
                          title={wo.releaseStatus ? "Released" : "Not Released"}
                          aria-label={`Release Status: ${
                            wo.releaseStatus ? "Released" : "Not Released"
                          }`}
                        >
                          {wo.releaseStatus ? "Released" : "Not Released"}
                        </span>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-gray-900">
                        <button
                          onClick={() => handleReleaseWorkOrder(wo)}
                          disabled={
                            isReadOnly || wo.releaseStatus || !canPlaceOnHold
                          }
                          className="py-1 px-2 rounded bg-[#365daf] hover:bg-blue-900 text-white text-xs font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                          title={
                            !canPlaceOnHold
                              ? "You do not have permission to release work orders"
                              : wo.releaseStatus
                              ? "Already released"
                              : "Release from hold"
                          }
                          aria-label={`Release work order ${wo.woNumber}`}
                        >
                          Release
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="14"
                      className="text-center py-8 sm:py-12 text-gray-600 text-sm sm:text-base"
                      aria-label="No work orders on hold"
                    >
                      No work orders on hold for this day
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="block sm:hidden mt-4 space-y-4">
            {paginatedWorkOrders.length > 0 ? (
              paginatedWorkOrders.map((wo) => (
                <div
                  key={wo.woNumber}
                  className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200 transition-all duration-200 opacity-70 hover:opacity-100"
                  style={getRowStyle(wo.allergen, wo.warehouse, null)}
                  tabIndex={0}
                  aria-label={`Work Order ${wo.woNumber}, Warehouse: ${wo.warehouse}`}
                >
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="font-semibold text-gray-700">Select</div>
                    <div>
                      <input
                        type="checkbox"
                        checked={selectedWorkOrders.has(wo.woNumber)}
                        onChange={() => handleSelectWorkOrder(wo.woNumber)}
                        disabled={isReadOnly || !wo.releaseStatus}
                        title={
                          wo.releaseStatus
                            ? "Selectable for transfer"
                            : "Must be released before transfer"
                        }
                        aria-label={`Select work order ${wo.woNumber} for transfer`}
                      />
                    </div>
                    <div className="font-semibold text-gray-700">
                      Scheduled Hour
                    </div>
                    <div className="select-text">{wo.scheduledTime}</div>
                    <div className="font-semibold text-gray-700">WO Number</div>
                    <div className="select-text">{wo.woNumber}</div>
                    <div className="font-semibold text-gray-700">Project #</div>
                    <div className="select-text">{wo.projectNumber}</div>
                    <div className="font-semibold text-gray-700">SKU</div>
                    <div className="select-text">{wo.sku || "N/A"}</div>
                    <div className="font-semibold text-gray-700">Title</div>
                    <div className="select-text">{wo.title}</div>
                    <div className="font-semibold text-gray-700">Label</div>
                    <div className="select-text">
                      {wo.labelCategory || "N/A"}
                    </div>
                    <div className="font-semibold text-gray-700">Allergen</div>
                    <div className="select-text">{wo.allergen || "None"}</div>
                    <div className="font-semibold text-gray-700">
                      Net Weight
                    </div>
                    <div className="select-text">{wo.netWeight || "N/A"}</div>
                    <div className="font-semibold text-gray-700">
                      Planned Qty
                    </div>
                    <div className="select-text">{wo.plannedQty || 0}</div>
                    <div className="font-semibold text-gray-700">
                      Actual Qty
                    </div>
                    <div className="select-text">{wo.actualQty || 0}</div>
                    <div className="font-semibold text-gray-700">
                      Lot Number
                    </div>
                    <div className="select-text">{wo.lotNumber || "N/A"}</div>
                    <div className="font-semibold text-gray-700">Warehouse</div>
                    <div className="select-text">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold text-gray-900 ${
                          wo.warehouse === "LAB HOLD"
                            ? "bg-[#fef08a]"
                            : "bg-[#f1bd99]"
                        }`}
                        title={
                          wo.warehouse === "LAB HOLD"
                            ? "On Lab Hold"
                            : "On QC Hold"
                        }
                        aria-label={`Warehouse: ${wo.warehouse}`}
                      >
                        {wo.warehouse}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-700">
                      Release Status
                    </div>
                    <div className="select-text">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold text-gray-900 ${
                          wo.releaseStatus ? "bg-green-200" : "bg-red-200"
                        }`}
                        title={wo.releaseStatus ? "Released" : "Not Released"}
                        aria-label={`Release Status: ${
                          wo.releaseStatus ? "Released" : "Not Released"
                        }`}
                      >
                        {wo.releaseStatus ? "Released" : "Not Released"}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-700">Action</div>
                    <div>
                      <button
                        onClick={() => handleReleaseWorkOrder(wo)}
                        disabled={
                          isReadOnly || wo.releaseStatus || !canPlaceOnHold
                        }
                        className="py-1 px-2 rounded bg-[#365daf] hover:bg-blue-900 text-white text-xs font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                        title={
                          !canPlaceOnHold
                            ? "You do not have permission to release work orders"
                            : wo.releaseStatus
                            ? "Already released"
                            : "Release from hold"
                        }
                      >
                        Release
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div
                className="text-center py-8 text-gray-600 text-sm"
                aria-label="No work orders on hold"
              >
                No work orders on hold for this day
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 disabled:opacity-50 text-xs sm:text-base"
                aria-label="Previous page"
              >
                Previous
              </button>
              <span className="text-gray-600 text-xs sm:text-base">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 disabled:opacity-50 text-xs sm:text-base"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          )}

          {(isReleaseModalOpen || isTransferModalOpen) && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
              <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl max-w-[95%] sm:max-w-lg w-full relative animate-fadeIn">
                {/* Close button (top right) */}
                <button
                  onClick={closeModal}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
                >
                  ✕
                </button>

                {/* Title */}
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">
                  {isReleaseModalOpen ? "Confirm Release" : "Confirm Transfer"}
                </h3>

                {/* Message */}
                <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
                  {isReleaseModalOpen
                    ? `Are you sure you want to release work order 
             #${workOrderToRelease?.woNumber}? It will be moved to 'Planned' status.`
                    : selectedWorkOrders.size === 0
                    ? "⚠️ Please select at least one released work order before proceeding."
                    : `Are you sure you want to transfer ${selectedWorkOrders.size} work order(s) to the store?`}
                </p>

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-4">
                  <button
                    onClick={closeModal}
                    className="py-2 px-4 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition"
                  >
                    Cancel
                  </button>
                  {(isReleaseModalOpen ||
                    (isTransferModalOpen && selectedWorkOrders.size > 0)) && (
                    <button
                      onClick={
                        isReleaseModalOpen ? confirmRelease : confirmTransfer
                      }
                      className="py-2 px-4 rounded-lg bg-[#365daf] hover:bg-blue-800 text-white text-sm font-semibold shadow-sm transition"
                    >
                      Confirm
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OnHold;
